/**
 * modelSatellites — turn a laid-out flow into that flow PLUS one model disc per
 * model-running node, joined by a dashed connector.
 *
 * Pure: takes nodes + the state inputs, returns new nodes/edges. The disc's
 * appearance comes entirely from `nodeModelState`, so the graph, the trigger
 * gate and the sub-agent readiness check can never disagree about whether a
 * node is ready.
 */
import { nodeCfgOf, nodeModelState, declaredModelSummary } from '../../../utils/nodeModel';
import { SAT_DX, SAT_DY } from './laneGeometry';

export const MODEL_NODE_PREFIX = '__model__';

/**
 * What the disc says on hover. The one place that turns a model STATE into a
 * sentence, so the caption and the explanation can never tell different stories.
 *
 * For a template-supplied default with no vendor yet, the tooltip spells the
 * RULE out in full ("Claude → haiku-4.5 · Codex → gpt-4o-mini · …") — that is
 * real declared data, so it explains the caption without the caption having to
 * pick one of the three and pretend it is settled.
 */
function discTooltip(label, state, modelBlocks, nodeId) {
  if (state.kind === 'locked') {
    return state.missingKey
      ? `${label} runs a model the template fixes, but there's no ${state.vendor} key — click to add one.`
      : `${label} runs a model the template fixes — the key is editable, the model isn't.`;
  }
  if (state.kind === 'default') {
    const rule = declaredModelSummary(modelBlocks, nodeId);
    if (state.needsVendor) {
      return `${label} runs the model the template recommends for whichever vendor this agent runs on`
        + (rule ? ` (${rule})` : '')
        + '. No vendor is chosen yet — click to choose one, or to pick a different model.';
    }
    return state.missingKey
      ? `${label} runs ${state.label} by default, but there's no ${state.vendor} key — click to add one.`
      : `${label} runs ${state.label} — the template's recommendation. Click to change it.`;
  }
  return `Model for ${label}`;
}

// Where the disc sits relative to its node: SEAT 0 of the node's attachment
// lane (laneGeometry — the one declaration the member satellites and the
// layouter read too). The layer channel is widened to fit (see
// MODEL_DISC_LAYER_SPACING).
const DISC_DX = SAT_DX;
const DISC_DY = SAT_DY;

/**
 * @param {object[]} nodes      laid-out React Flow nodes
 * @param {object[]} edges      laid-out React Flow edges
 * @param {object}   graph      the backend graph (for nodeConfigs)
 * @param {object}   ctx        { overrides, agentEnvKeys, projectSecrets,
 *                              projectVendor, modelBlocks, flagged, onPick }
 * @returns {{nodes: object[], edges: object[]}}
 */
export function withModelSatellites(nodes, edges, graph, ctx) {
  if (!Array.isArray(nodes) || nodes.length === 0) return { nodes, edges };
  const {
    overrides, agentEnvKeys, projectSecrets, projectVendor, modelBlocks, flagged, onPick,
    agentVendor,
  } = ctx || {};
  // Node ids the Trigger check pointed at — their discs get the warm ring so
  // the scroll lands on something findable.
  const flaggedSet = flagged instanceof Set ? flagged : new Set(flagged || []);

  const discNodes = [];
  const discEdges = [];

  for (const n of nodes) {
    if (!n?.data?.runsModel) continue;
    const cfg = nodeCfgOf(graph, n.id);
    const state = nodeModelState({
      cfg,
      nodeId: n.id,
      override: overrides?.[n.id],
      agentEnvKeys,
      projectSecrets,
      projectVendor,
      modelBlocks,
      agentVendor,
      overrides,
    });
    const id = `${MODEL_NODE_PREFIX}${n.id}`;
    discNodes.push({
      id,
      type: 'modelNode',
      draggable: false,
      selectable: false,
      position: { x: (n.position?.x || 0) + DISC_DX, y: (n.position?.y || 0) + DISC_DY },
      data: {
        state,
        flagged: flaggedSet.has(n.id),
        tooltip: discTooltip(n.data?.label || n.id, state, modelBlocks, n.id),
        // The disc ALWAYS owns its click, even when it opens nothing. Leaving
        // the handler off doesn't make the click inert — it makes the editor
        // fall through to the generic branch and open the node-config modal
        // for the disc's SYNTHETIC id, which has no config: a blank
        // "Node — Configuration" panel over a locked model.
        //
        // A template-DEFAULT node is ALWAYS clickable, vendor or not — with no
        // vendor resolved, choosing one is the entire point of the click, and
        // that is exactly the state the run refuses in. A truly LOCKED node
        // (identity row / vendor pin) is clickable only for its KEY, so with no
        // resolvable vendor there is genuinely nothing to ask for and it stays
        // inert.
        onNodeClick: !onPick || (state.kind === 'locked' && !state.vendor)
          ? () => {}
          : () => onPick(n.id, n.data?.label || n.id, state),
      },
    });
    discEdges.push({
      id: `e${id}`,
      source: n.id,
      sourceHandle: 'bottom',
      target: id,
      targetHandle: 'disc',
      // Orthogonal elbow, like every other edge on this canvas. A free bezier
      // between a DOWN-facing source and a RIGHT-facing target swings wide and
      // low before doubling back, which reads as a disconnected stub floating
      // near the disc rather than a line joining it to its node.
      type: 'smoothstep',
      pathOptions: { borderRadius: 12 },
      focusable: false,
      selectable: false,
      style: {
        stroke: (state.kind === 'nokey' || state.missingKey)
          ? '#d9534f' : 'rgba(190, 190, 190, 0.85)',
        strokeWidth: 1.5,
        strokeDasharray: '5 4',
      },
      data: {
        // The run count of the node this disc belongs to, on the connector —
        // the SAME key `memberSatellites` stamps and ReadonlyLabeledEdge lights
        // FibreBeams from. Without it this line was the one edge on a running
        // canvas that stayed dark: the node pulsed, its model sat unlit beside
        // it, and the work visibly happening in the model looked idle.
        // (Founder, 2026-09-06, on the Copilot: "肯定是点亮开始那个线和虚线啊".)
        activeRuns: Math.max(0, Number(n.data?.activeRuns) || 0),
      },
    });
  }

  if (discNodes.length === 0) return { nodes, edges };
  return { nodes: [...nodes, ...discNodes], edges: [...edges, ...discEdges] };
}
